import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { formatBotNumber, type WhatsAppLinkCode } from '@domain/entities/WhatsAppLink';
import { getServices } from '@infrastructure/services';
import { Banner, Button, Chip, Spinner } from '../../design-system';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import styles from './WhatsAppLinkCard.module.css';

/**
 * Vincular el WhatsApp propio al asistente, desde «Mi perfil › Avisos».
 *
 * Es la única puerta por la que un número llega a ser alguien: aquí hay una
 * sesión de Google, y el código que sale de aquí se manda desde el teléfono. El
 * asistente le cree al número sólo después de haber visto las dos cosas.
 *
 * La pantalla no sabe cuándo llegó el código —eso pasa en otro aparato—, así que
 * mientras hay uno vivo **pregunta** cada pocos segundos si ya quedó. Pregunta y
 * no escucha, a propósito: en la red de la oficina el canal de escucha de
 * Firestore no conecta, y las llamadas de ida y vuelta sí.
 *
 * **El código QR lleva el mismo enlace que el botón**, nada más: quien está en
 * su computadora lo escanea con el teléfono y se le abre WhatsApp con el mensaje
 * ya escrito. Lo pidió JC el 6 oct 2026, después de vincular el suyo copiando el
 * código a mano. Se dibuja aquí, en el navegador: el código de vincular no se le
 * manda a ningún servicio para que lo convierta en imagen. Y WhatsApp no envía
 * solo: deja el mensaje escrito y espera el toque de quien lo abrió.
 *
 * **Vincular es también aceptar los avisos** —que te nombraron respaldo, que
 * tienes algo por firmar, que tu solicitud avanzó—, y la tarjeta lo dice antes
 * del botón. Meta pide que a nadie se le escriba sin haberlo aceptado, y la
 * forma de dejar de recibirlos es la misma de siempre: desvincular.
 */

/** Cada cuánto se pregunta si el código ya llegó, mientras hay uno a la vista. */
const POLL_EVERY_MS = 4000;

const TIME = new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

export const WhatsAppLinkCard = (): ReactNode => {
  const session = useSession();
  const toast = useToast();

  const [code, setCode] = useState<WhatsAppLinkCode | null>(null);
  const [busy, setBusy] = useState(false);

  const status = useQuery({
    queryKey: ['whatsapp-link', session.personId],
    queryFn: async () => {
      const result = await getServices().whatsapp.status();
      return result.ok ? result.value : null;
    },
    refetchInterval: code === null ? false : POLL_EVERY_MS,
  });

  const linked = status.data?.linked === true;

  /* Vinculado: el código ya cumplió y no hay por qué seguir enseñándolo. */
  useEffect(() => {
    if (linked) setCode(null);
  }, [linked]);

  /* Vencido: se retira solo, para no dejar a la vista un código que el asistente
     ya no acepta. */
  useEffect(() => {
    if (code === null) return undefined;
    const remaining = code.expiresAt.getTime() - Date.now();
    const timer = window.setTimeout(() => {
      setCode(null);
    }, Math.max(0, remaining));
    return () => {
      window.clearTimeout(timer);
    };
  }, [code]);

  const start = async (): Promise<void> => {
    setBusy(true);
    const result = await getServices().whatsapp.start();
    setBusy(false);
    if (!result.ok) {
      toast.bad('No se pudo generar el código. Inténtalo de nuevo.');
      return;
    }
    setCode(result.value);
  };

  const unlink = async (): Promise<void> => {
    setBusy(true);
    const result = await getServices().whatsapp.unlink();
    setBusy(false);
    if (!result.ok) {
      toast.bad('No se pudo desvincular. Inténtalo de nuevo.');
      return;
    }
    toast.ok('Tu WhatsApp quedó desvinculado del asistente.');
    await status.refetch();
  };

  return (
    <section className={styles.card} aria-label="Asistente de WhatsApp">
      <div className={styles.head}>
        <h3 className={styles.title}>Asistente de WhatsApp</h3>
        {status.data !== undefined && status.data !== null && (
          <Chip
            background={linked ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'}
            foreground={linked ? 'var(--color-success-ink)' : 'var(--color-text-secondary)'}
          >
            {linked ? 'Vinculado' : 'Sin vincular'}
          </Chip>
        )}
      </div>

      <p className={styles.note}>
        Pregúntale cuántos días te quedan, pide vacaciones o revisa cómo va tu solicitud, escribiéndole
        como a un compañero. Antes de enviar o cambiar algo te enseña un resumen y te pide confirmar.
      </p>
      {/* Vincular es también aceptar que le escriba: se dice antes de vincular, no después. */}
      <p className={styles.note}>
        También te avisa por ahí cuando hay una novedad en tus vacaciones o cuando alguien necesita tu
        respuesta: cubrir a un compañero o firmar una solicitud.
      </p>

      {status.isPending ? (
        <Spinner label="Consultando tu vínculo" />
      ) : status.data === null || status.data === undefined ? (
        <Banner tone="warn">No se pudo consultar si tu WhatsApp está vinculado. Recarga la página.</Banner>
      ) : linked ? (
        <>
          <p className={styles.state}>
            Tu número terminado en <strong>{status.data.numberTail ?? '····'}</strong> está vinculado
            {status.data.linkedAt === null ? '' : ` desde el ${DAY.format(status.data.linkedAt)}`}.
          </p>
          <p className={styles.hint}>
            Lo que hagas por WhatsApp queda registrado a tu nombre, igual que en el portal. Si pierdes o
            cambias tu teléfono, o ya no quieres recibir avisos ahí, desvincúlalo aquí.
          </p>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              small
              busy={busy}
              onClick={() => {
                void unlink();
              }}
            >
              Desvincular
            </Button>
          </div>
        </>
      ) : code === null ? (
        <div className={styles.actions}>
          <Button
            small
            busy={busy}
            onClick={() => {
              void start();
            }}
          >
            Vincular mi WhatsApp
          </Button>
        </div>
      ) : (
        <>
          {code.deepLink !== null && (
            <>
              <div className={styles.scan}>
                <div className={styles.qr}>
                  <QRCodeSVG
                    className={styles.qrImage}
                    value={code.deepLink}
                    level="M"
                    marginSize={2}
                    bgColor="transparent"
                    fgColor="currentColor"
                    title="Código QR que abre WhatsApp con el mensaje para vincular"
                  />
                </div>
                <div className={styles.scanText}>
                  <p className={styles.scanLead}>Escanea este código con la cámara de tu teléfono.</p>
                  <p className={styles.hint}>
                    Se abre WhatsApp con el mensaje ya escrito: sólo te falta enviarlo.
                  </p>
                </div>
              </div>
              <p className={styles.manual}>Si no puedes escanearlo:</p>
            </>
          )}

          <ol className={styles.steps}>
            <li className={styles.step}>
              {code.botNumber === null ? (
                <span>Abre WhatsApp y escríbele al asistente.</span>
              ) : (
                <span>
                  Abre WhatsApp y escríbele al asistente, al{' '}
                  <strong className={styles.number}>{formatBotNumber(code.botNumber)}</strong>.
                </span>
              )}
            </li>
            <li className={styles.step}>
              <span>Mándale este código:</span>
              <output className={styles.code} aria-label="Código para vincular">
                {code.code}
              </output>
            </li>
          </ol>

          {code.botNumber === null && (
            <Banner tone="warn">
              El asistente todavía no tiene un número de WhatsApp configurado, así que este código no se
              puede mandar a ningún lado. Avísale a quien administra el portal.
            </Banner>
          )}

          <p className={styles.hint}>
            El código vence a las {TIME.format(code.expiresAt)} y sirve una sola vez. Esta pantalla se
            actualiza sola cuando el asistente lo recibe.
          </p>

          <div className={styles.actions}>
            {code.deepLink !== null && (
              <a className={styles.open} href={code.deepLink} target="_blank" rel="noreferrer">
                Abrir WhatsApp con el código
              </a>
            )}
            <Button
              variant="ghost"
              small
              busy={busy}
              onClick={() => {
                void start();
              }}
            >
              Pedir otro código
            </Button>
          </div>
        </>
      )}
    </section>
  );
};
